import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiClient } from "@/lib/api/client";
import { BlockDto, CreateBlockDto, UpdateBlockDto, DistrictDto, BlockFilterParams } from "@/lib/types/api";
import { showToast } from "@/lib/utils/toast";
import { handleApiError } from "@/lib/utils/errors";
import { SEARCH_DEBOUNCE_MS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DataTable, Column } from "@/components/datatable/DataTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Map, Search } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { LoadingButton } from "@/components/ui/loading";
import { StatusBadge } from "@/components/ui/status-badge";

const blockSchema = z.object({
  blockName: z.string().min(1, "Block name is required"),
  lgdCode: z.string().regex(/^\d*$/, { message: "LGD code must be numeric" }).optional(),
  districtId: z.string().min(1, "District is required"),
});

type BlockFormData = z.infer<typeof blockSchema>;

export default function BlocksPage() {
  const [blocks, setBlocks] = useState<BlockDto[]>([]);
  const [districts, setDistricts] = useState<DistrictDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string>("blockName");
  const [sortDescending, setSortDescending] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<BlockFormData>({ resolver: zodResolver(blockSchema) });

  const loadBlocks = useCallback(async () => {
    try {
      setIsLoading(true);
      
      const filterParams: BlockFilterParams = {
        pageNumber: currentPage,
        pageSize: pageSize,
        searchTerm: searchQuery || undefined,
        sortBy: sortBy,
        sortDescending: sortDescending,
      };
      
      const response = await apiClient.getBlocksPagedWithFilters(filterParams);
      
      if (response.success && response.data) {
        setBlocks(response.data.items || []);
        setTotalCount(response.data.totalCount || 0);
        setTotalPages(Math.ceil((response.data.totalCount || 0) / pageSize));
      }
    } catch (err: unknown) {
      showToast.error(handleApiError(err, "Failed to load blocks"));
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchQuery, sortBy, sortDescending]);

  // Single effect to handle all data loading with proper debouncing
  useEffect(() => {
    // For page/pageSize changes, load immediately without debounce
    if (searchQuery === "" && sortBy === "blockName" && !sortDescending) {
      loadBlocks();
      return;
    }

    // For search/sort changes, debounce
    const timer = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        loadBlocks();
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [currentPage, pageSize, searchQuery, sortBy, sortDescending, loadBlocks]);

  useEffect(() => {
    loadDistricts();
  }, []);

  const loadDistricts = async () => {
    try {
      const response = await apiClient.getDistricts();
      if (response.success && response.data) {
        setDistricts(response.data);
      }
    } catch (err: unknown) {
      showToast.error(handleApiError(err, "Failed to load districts"));
    }
  };

  const getDistrictName = (districtId: string) => {
    return districts.find((d) => d.id === districtId)?.districtName || "Unknown";
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDescending(!sortDescending);
    } else {
      setSortBy(column);
      setSortDescending(false);
    }
  };

  const onSubmit = async (data: BlockFormData) => {
    try {
      setIsSubmitting(true);
      const lgdCodeValue = data.lgdCode ? Number(data.lgdCode) : undefined;

      if (editingId) {
        const updateData: UpdateBlockDto = {
          blockName: data.blockName,
          lgdCode: lgdCodeValue,
          isActive: true,
        };
        await apiClient.updateBlock(editingId, updateData);
        showToast.success("Block updated successfully");
      } else {
        const createData: CreateBlockDto = {
          blockName: data.blockName,
          lgdCode: lgdCodeValue,
          districtId: data.districtId,
        };
        await apiClient.createBlock(createData);
        showToast.success("Block created successfully");
      }
      setShowForm(false);
      setEditingId(null);
      reset();
      loadBlocks();
    } catch (err: unknown) {
      showToast.error(handleApiError(err, editingId ? "Failed to update block" : "Failed to create block"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (block: BlockDto) => {
    setEditingId(block.id);
    reset({
      blockName: block.blockName,
      lgdCode: block.lgdCode != null ? String(block.lgdCode) : "",
      districtId: block.districtId,
    });
    setShowForm(true);
  };

  const handleDeleteRequest = (id: string) => {
    setDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await apiClient.deleteBlock(deleteId);
      showToast.success("Block deleted successfully");
      setDeleteId(null);
      loadBlocks();
    } catch (err: unknown) {
      showToast.error(handleApiError(err, "Failed to delete block"));
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    reset();
  };

  const handleOpenNew = () => {
    setEditingId(null);
    reset({ blockName: "", lgdCode: "", districtId: "" });
    setShowForm(true);
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const columns: Column<BlockDto>[] = [
    {
      key: "blockName",
      header: "Block Name",
      sortable: true,
      render: (b) => <span className="font-medium text-gray-900 dark:text-gray-100">{b.blockName}</span>,
    },
    {
      key: "lgdCode",
      header: "LGD Code",
      sortable: true,
      render: (b) => <span className="text-sm text-gray-600 dark:text-gray-400">{b.lgdCode ?? <span className="text-gray-400">—</span>}</span>,
    },
    {
      key: "district",
      header: "District",
      sortable: true,
      render: (b) => <span className="text-sm text-gray-600 dark:text-gray-400">{getDistrictName(b.districtId)}</span>,
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (b) => <StatusBadge isActive={b.isActive} />,
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      render: (b) => <span className="text-sm text-gray-500 dark:text-gray-400">{formatDate(b.createdAt)}</span>,
    },
  ];

  return (
    <div className="space-y-6">
      <Dialog open={showForm} onOpenChange={(open) => !open && handleCancel()}>
        <DialogContent onClose={handleCancel}>
          <DialogHeader>
            <DialogTitle>{editingId ? "Edit Block" : "New Block"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <Input
              label="Block Name"
              placeholder="e.g., Central Block"
              error={errors.blockName?.message}
              {...register("blockName")}
            />
            <Input
              label="LGD Code (Optional)"
              placeholder="Optional code"
              error={errors.lgdCode?.message}
              {...register("lgdCode")}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">District</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                {...register("districtId")}
                disabled={!!editingId}
              >
                <option value="">Select a district</option>
                {districts.map((d) => (
                  <option key={d.id} value={d.id}>{d.districtName}</option>
                ))}
              </select>
              {errors.districtId && <p className="text-red-500 text-sm mt-1">{errors.districtId.message}</p>}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>Cancel</Button>
              <LoadingButton type="submit" isLoading={isSubmitting} className="bg-primary px-4 py-2 rounded hover:bg-primary/90">
                {editingId ? "Update" : "Create"}
              </LoadingButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent onClose={() => setDeleteId(null)}>
          <DialogHeader>
            <DialogTitle>Delete Block</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600 dark:text-gray-400">Are you sure you want to delete this block? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="shadow-sm">
        <CardHeader className="border-b bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <Map className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <CardTitle className="text-2xl">Blocks</CardTitle>
                <CardDescription className="mt-1">Manage block information</CardDescription>
              </div>
            </div>
            <Button onClick={handleOpenNew} className="shadow-sm">
              <Plus className="mr-2 h-4 w-4" />
              Add Block
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="mb-6 space-y-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Search</h3>
              {totalCount > 0 && (
                <div className="flex items-center gap-2 px-2.5 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                  <span>{totalCount}</span>
                  <span>Total Blocks</span>
                </div>
              )}
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search blocks..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-10"
              />
            </div>
            {searchQuery && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600 dark:text-gray-400">Active filters:</span>
                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-md text-xs">
                    Search: {searchQuery}
                    <button onClick={() => setSearchQuery("")} className="hover:text-blue-900 dark:hover:text-blue-100">×</button>
                  </span>
                  <button
                    onClick={() => setSearchQuery("")}
                    className="text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 underline"
                  >
                    Clear all
                  </button>
                </div>
              </div>
            )}
          </div>

          <DataTable
            data={blocks}
            columns={columns}
            currentPage={currentPage}
            pageSize={pageSize}
            totalCount={totalCount}
            totalPages={totalPages}
            loading={isLoading}
            sortBy={sortBy}
            sortDescending={sortDescending}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onPageSizeChange={(size) => setPageSize(size)}
            onRefresh={loadBlocks}
            getRowKey={(b) => b.id}
            actionsRenderer={(b) => (
              <div className="flex justify-end gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleEdit(b)}
                  className="h-8 w-8 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-600 dark:hover:text-blue-400"
                  title="Edit Block"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleDeleteRequest(b.id)}
                  className="h-8 w-8 p-0 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400"
                  title="Delete Block"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
            emptyMessage={searchQuery ? "No blocks match your search" : "No blocks found"}
          />
        </CardContent>
      </Card>
    </div>
  );
}
